import * as React from 'react'

import { Box, Button, InputCheckBox, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import Importer from 'modules/employees/components/Importer'
import type { EmployeeImporterContextProps } from 'modules/employees/lib/employeesImporterContext'
import { TargetBalance } from 'modules/timeoff/lib/enums'

import useProcessFile from './useProcessFile'

const DOWNLOAD_PATH = '/onboarding_importer/imports_timeoff'
const CHECK_DOWNLOAD = '/onboarding_importer/download_available_timeoff'
interface Props {
  importer: EmployeeImporterContextProps
  onClose: () => void
}

const ImportTimeoffModalPage = ({ importer, onClose }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const api = React.useMemo(() => ApiClient(), [])
  const { running: isDowloading, performAction: onDownloadRequest } = useAsyncAction(
    async () => {
      const response = await api.get(CHECK_DOWNLOAD)

      if (response.result) {
        window.location.href = buildUrl(DOWNLOAD_PATH)
      } else {
        app.setFlash({
          type: 'correct',
          message: i18n.t('employees.export_template_by_email'),
        })
      }
    },
    {
      successFlash: false,
    }
  )

  const { running, onSubmit } = useProcessFile({
    api,
  })

  const title = i18n.t('employees.import_timeoff_title')
  const description = i18n.t('import.description')
  const options = [
    {
      value: TargetBalance.Available.toString(),
      label: i18n.t('time_off.incidences.available'),
    },
    {
      value: TargetBalance.Accrued.toString(),
      label: i18n.t('time_off.incidences.accrued'),
    },
  ]

  const onModalClose = () => {
    importer.setFiles([])
    importer.setTargetBalance(TargetBalance.Available)
    onClose()
  }

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onModalClose}>
      <Modal
        illustration="clock-app"
        title={title}
        description={description}
        onClose={onModalClose}
        onSubmit={onSubmit}
        footer={
          <Button disabled={!importer.hasFile || running} submit>
            {i18n.t('import.process_file')}
          </Button>
        }
      >
        <Box gap="s24">
          <Box flexDirection="row" justifyContent="center">
            <Button onClick={onDownloadRequest} type="secondary" disabled={isDowloading}>
              {i18n.t('import.download_timeoff_template')}
            </Button>
          </Box>
          <Importer importer={importer} />
          {hasFeature('timeoff_manual_adjustment_options') ? (
            <Select
              label={i18n.t('time_off.incidences.adjust_from')}
              options={options}
              mandatory
              onChange={(value: string) => {
                importer.setTargetBalance(value as TargetBalance)
              }}
              value={importer.targetBalance}
            />
          ) : (
            ''
          )}

          <Box flexDirection="row" gap="s8" alignItems="center">
            <InputCheckBox
              checked={importer.skipNotifications}
              onClick={() => importer.setSkipNotifications(!importer.skipNotifications)}
            />
            <Text.Normal>{i18n.t('time_off.incidences.skip_notifications_multi')}</Text.Normal>
          </Box>
        </Box>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(ImportTimeoffModalPage)
